import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core'
import * as Highcharts from 'highcharts'
import 'highcharts/modules/accessibility'
import { HighchartsChartModule } from 'highcharts-angular'

import { GetTrygroupPersonalAnalysisQuery } from '../../../../../_generated/graphql'
import { KA_TREND_LABELS } from '../ka-labels'

type ScoreTrend = NonNullable<
  GetTrygroupPersonalAnalysisQuery['trygroupPersonalAnalysis']['scoreTrend']
>

const KA_COLORS = [
  '#e77561',
  '#dca664',
  '#e7d46e',
  '#88bd94',
  '#8dcbc9',
  '#69b2c5',
  '#839be1',
  '#bd8dd1',
]

const OVERALL_COLOR = '#1e3a5f'
const OVERALL_SERIES_NAME = '面入スコア(100点満点)'
const CHART_HEIGHT = 400

@Component({
  selector: 'app-trygroup-score-trend-chart',
  standalone: true,
  imports: [HighchartsChartModule],
  template: `
    <div class="score-trend-chart" #container>
      <highcharts-chart
        [Highcharts]="Highcharts"
        [options]="options"
        [callbackFunction]="chartCallback"
      />
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .score-trend-chart {
        width: 100%;
        overflow-x: hidden;
      }

      highcharts-chart {
        display: block;
        width: 100%;
        height: ${CHART_HEIGHT}px;
      }
    `,
  ],
})
export class TrygroupScoreTrendChartComponent
  implements OnChanges, AfterViewInit, OnDestroy
{
  @Input({ required: true }) scoreTrend!: ScoreTrend
  @ViewChild('container') container!: ElementRef

  chart: Highcharts.Chart | undefined = undefined
  readonly Highcharts = Highcharts
  options!: Highcharts.Options

  private timerId: ReturnType<typeof setTimeout> | null = null
  private readonly observer: ResizeObserver = new ResizeObserver(() => {
    if (this.chart && this.container?.nativeElement?.offsetParent != null) {
      this.chart.reflow()
    }
  })

  ngAfterViewInit(): void {
    this.timerId = setTimeout(() => {
      this.observer.observe(this.container?.nativeElement)
      this.timerId = null
    }, 100)
  }

  ngOnDestroy(): void {
    if (this.timerId != null) {
      clearTimeout(this.timerId)
    }
    this.observer.disconnect()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['scoreTrend']) {
      if (this.chart) {
        this.updateChart()
      } else {
        this.buildOptions()
      }
    }
  }

  chartCallback: Highcharts.ChartCallbackFunction = (chart): void => {
    this.chart = chart
    this.updateChart()
  }

  private buildCategories(): string[] {
    const len = this.scoreTrend.dataPoints.length
    return this.scoreTrend.dataPoints.map((_, i) => `${len - i}回前`)
  }

  private buildOptions(): void {
    this.options = {
      chart: {
        type: 'line',
        height: CHART_HEIGHT,
        spacing: [16, 16, 16, 16],
      },
      credits: { enabled: false },
      title: { text: undefined },
      xAxis: {
        categories: this.buildCategories(),
        labels: {
          style: { fontSize: '11px', color: '#666' },
        },
      },
      yAxis: [
        {
          title: { text: undefined },
          min: 0,
          max: 5,
          tickPositions: [0, 1, 2, 3, 4, 5],
          gridLineDashStyle: 'Dash',
          labels: {
            style: { fontSize: '11px', color: '#666' },
          },
        },
        {
          title: { text: undefined },
          min: 0,
          max: 100,
          tickPositions: [0, 25, 50, 75, 100],
          gridLineWidth: 0,
          opposite: true,
          labels: {
            style: { fontSize: '11px', color: '#666' },
          },
        },
      ],
      tooltip: {
        shared: true,
        useHTML: true,
        formatter: function () {
          const points = this.points
          if (!points || points.length === 0) {
            return ''
          }

          let html = `<div style="font-size:13px;font-weight:bold;margin-bottom:4px">${points[0].key}</div>`

          for (const point of points) {
            const color = point.color as string
            const name = point.series.name
            const value = point.y

            if (name === OVERALL_SERIES_NAME) {
              html += `<div style="color:${color};font-weight:bold;margin-top:4px">${name}：${value?.toFixed(1)}点</div>`
            } else {
              html += `<div style="color:${color}">${name}：${value?.toFixed(2)}</div>`
            }
          }

          return html
        },
      },
      legend: {
        align: 'center',
        verticalAlign: 'bottom',
        layout: 'horizontal',
        itemStyle: { fontSize: '11px' },
      },
      plotOptions: {
        series: {
          connectNulls: true,
        },
      },
      series: [],
    }
  }

  private updateChart(): void {
    if (!this.chart) {
      return
    }

    const dataPoints = this.scoreTrend.dataPoints

    const kaSeries: Highcharts.SeriesOptionsType[] = KA_TREND_LABELS.map(
      (ka, index) => ({
        name: ka.label,
        type: 'line' as const,
        yAxis: 0,
        color: KA_COLORS[index],
        lineWidth: 1.5,
        marker: { radius: 3, symbol: 'circle' as const },
        data: dataPoints.map((dp) => {
          const val = dp.keyActionScores[ka.key]
          return val != null ? val : null
        }),
      }),
    )

    const overallSeries: Highcharts.SeriesOptionsType = {
      name: OVERALL_SERIES_NAME,
      type: 'line',
      yAxis: 1,
      color: OVERALL_COLOR,
      lineWidth: 3,
      marker: { radius: 5, symbol: 'circle' },
      data: dataPoints.map((dp) =>
        dp.overallScore != null ? Math.round(dp.overallScore * 10) / 10 : null,
      ),
    }

    this.chart.update(
      {
        xAxis: { categories: this.buildCategories() },
        series: [...kaSeries, overallSeries],
      },
      true,
      true,
    )
  }
}
